import React from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import styles from './button.module.scss';
import { Icons } from '../';
import { applyClasses, applyProps } from '../shared/js';

const Button = ({
  id,
  href,
  disabled,
  children,
  style,
  className,
  newTab,
  type,
  replace,
  ...props
}) => {
  const handleClick = (e) => {
    e.currentTarget.blur();
    props.onClick && props.onClick(e); // eslint-disable-line
  };
  if (href) {
    return typeof href === 'string' &&
      (href.includes('http://') ||
        href.includes('https://') ||
        href.includes('javascript')) ? (
      <a
        id={id}
        href={href}
        className={cn(
          { [styles.button]: !props.link },
          applyClasses(props, styles),
          className
        )}
        style={style}
        target={href.includes('javascript') ? '_self' : newTab && '_blank'}
        rel={newTab && 'noopener norefferer'}
        /* eslint-disable react/no-unknown-property */
        replace={replace}
        {...applyProps(props)}
      >
        <>
          {children}
          {newTab && (
            <span className={styles.newTabIcon}>
              <Icons.NewWindow />
            </span>
          )}
        </>
      </a>
    ) : (
      <Link
        id={id}
        to={href}
        className={cn(
          { [styles.button]: !props.link },
          applyClasses(props, styles),
          className
        )}
        style={style}
        target={newTab && '_blank'}
        rel={newTab && 'noopener norefferer'}
        replace={replace}
        {...applyProps(props)}
      >
        <>
          {children}
          {newTab && (
            <span className={styles.newTabIcon}>
              <Icons.NewWindow />
            </span>
          )}
        </>
      </Link>
    );
  }
  if (props.link) {
    return (
      <a
        id={id}
        className={cn(
          { [styles.button]: !props.link },
          applyClasses(props, styles),
          className
        )}
        style={style}
        disabled={disabled}
        {...applyProps(props)}
        onClick={handleClick}
        role="button"
      >
        {props.underline ? (
          <>
            {children}
            {!props.link && <div className={styles.underlineElement} />}
          </>
        ) : (
          children
        )}
      </a>
    );
  }
  return (
    <button
      id={id}
      className={cn(
        { [styles.button]: !props.link },
        applyClasses(props, styles),
        className
      )}
      style={style}
      type={type}
      disabled={disabled}
      {...applyProps(props)}
      onClick={handleClick}
    >
      {props.underline ? (
        <>
          {children}
          {!props.link && <div className={styles.underlineElement} />}
        </>
      ) : (
        children
      )}
    </button>
  );
};

Button.propTypes = {
  /** The ID of the button. Useful to select the button during testing. */
  id: PropTypes.string,
  /** Turns the button into a link pointing at the specified URL */
  href: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** The content of the button. Could be an image, plain text, another react component, or anything! */
  children: PropTypes.any,
  /** Apply primary styles to the button */
  primary: PropTypes.bool,
  /** Apply secondary styles to the button */
  secondary: PropTypes.bool,
  /** Replace the background of the button with an outline, and inherit the primary or secondary colors */
  outline: PropTypes.bool,
  /** Remove the background and border from the button */
  link: PropTypes.bool,
  /** Force the color of the button to black */
  black: PropTypes.bool,
  /** Force the color of the button to white */
  white: PropTypes.bool,
  /** Increases the text size of the button on hover */
  zoom: PropTypes.bool,
  /** Fixes the height of the button. Combine with Zoom to increase the label of the button without increasing its overall size */
  fixedHeight: PropTypes.bool,
  /** Stretch the button to fill the width of its container */
  fullWidth: PropTypes.bool,
  /** Styles object passed down to the button element */
  style: PropTypes.object,
  /** Custom class name passed down to the button element */
  className: PropTypes.string,
  /** The button type, set to "submit" to submit a form with it. */
  type: PropTypes.string,
  /** Sets the disabled property on the button */
  disabled: PropTypes.bool,
  /** Whether or not this button should open its link in a new tab. Does not affect non-link buttons. */
  newTab: PropTypes.bool,
  /** Adds the animated underline style to the button */
  underline: PropTypes.bool,
  /** When creating a link, use this prop to replace the current page in the history */
  replace: PropTypes.bool,
};

Button.defaultProps = {
  href: undefined,
  children: undefined,
  primary: undefined,
  secondary: undefined,
  outline: undefined,
  link: undefined,
  black: undefined,
  white: undefined,
  zoom: undefined,
  fullWidth: undefined,
  fixedHeight: undefined,
  className: undefined,
  type: 'button',
  style: undefined,
  disabled: undefined,
  newTab: undefined,
  underline: undefined,
  replace: undefined,
};

export default Button;
